import { Anchor, ArrowUpRight, Sparkles } from "lucide-react";

const ROUTER = "https://affiliate.eigooinc.net/go";

const FLEET = [
  {
    slug: "island-gold",
    name: "Island Gold",
    category: "Artisanal Provisions",
    descriptor: "Curated provisions · luxury sourcing · slow craft",
    accent: "gold",
    sigil: "IG",
  },
  {
    slug: "siren-hub",
    name: "Siren Hub",
    category: "Creator Contests",
    descriptor: "Creator competitions · prize pools · sovereign attention",
    accent: "violet",
    sigil: "SH",
  },
  {
    slug: "lead-sentry",
    name: "Lead Sentry",
    category: "Business Armor",
    descriptor: "Pipeline defense · lead discipline · operator-grade CRM",
    accent: "gold",
    sigil: "LS",
  },
  {
    slug: "mssheila-productions",
    name: "MsSheila Productions",
    category: "Music Enterprise",
    descriptor: "Catalogue ownership · publishing · sovereign sound",
    accent: "violet",
    sigil: "MP",
  },
];

export default function FleetAlliance() {
  return (
    <section id="fleet-alliance" data-testid="fleet-alliance">
      {/* Header */}
      <div className="mb-7 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-violet animate-pulse-violet" />
            <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-violet-soft">
              Fleet Alliance · Ecosystem
            </span>
          </div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-white tracking-tight">
            Sister Nodes · <span className="gold-text">Lineage Active</span>
          </h2>
          <p className="mt-3 max-w-2xl text-white/55 leading-relaxed">
            The Armada does not sail alone. Each node in the fleet is a sovereign
            asset of the ecosystem. Engagements route through the EIGOO Router and
            settle under the <span className="text-gold">75/15/10 Lineage Law</span>.
          </p>
        </div>
        <div
          className="hidden sm:flex flex-col items-end gap-1"
          data-testid="lineage-law-badge"
        >
          <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-white/45">
            Lineage Law
          </div>
          <div className="font-mono text-sm tracking-[0.22em] gold-text">
            75 / 15 / 10
          </div>
        </div>
      </div>

      {/* Roster */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {FLEET.map((f, i) => {
          const isGold = f.accent === "gold";
          return (
            <a
              key={f.slug}
              href={`${ROUTER}/${f.slug}`}
              target="_blank"
              rel="noopener sponsored"
              data-testid={`fleet-link-${i + 1}`}
              data-track-slug={f.slug}
              className="group relative rounded-2xl border border-white/8 bg-[#080808] p-6 sm:p-7 overflow-hidden hover:border-gold/40 hover:-translate-y-0.5 transition-all duration-300"
            >
              {/* Soft accent glow */}
              <div
                className={`absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-50 group-hover:opacity-80 transition-opacity ${
                  isGold ? "bg-gold/10" : "bg-violet/10"
                }`}
              />
              {/* Top hairline */}
              <span
                className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent ${
                  isGold ? "via-gold/70" : "via-violet/70"
                } to-transparent opacity-60 group-hover:opacity-100 transition-opacity`}
              />

              <div className="relative flex items-start gap-5">
                {/* Sigil */}
                <div
                  className={`shrink-0 w-14 h-14 rounded-lg grid place-items-center border ${
                    isGold ? "border-gold/40 bg-gold/5" : "border-violet/40 bg-violet/5"
                  }`}
                >
                  <span
                    className={`font-heading text-lg tracking-[0.15em] ${
                      isGold ? "text-gold" : "text-violet-soft"
                    }`}
                  >
                    {f.sigil}
                  </span>
                </div>

                {/* Copy */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-mono text-[10px] tracking-[0.26em] uppercase ${
                        isGold ? "text-gold/90" : "text-violet-soft"
                      }`}
                    >
                      {f.category}
                    </span>
                  </div>
                  <div className="mt-1 font-heading text-2xl text-white leading-tight">
                    {f.name}
                  </div>
                  <p className="mt-2 text-sm text-white/55 leading-relaxed">
                    {f.descriptor}
                  </p>

                  <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-white/65 group-hover:text-gold transition-colors">
                      Engage Signal
                      <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </span>
                    <span className="inline-flex items-center gap-1 font-mono text-[9px] tracking-[0.22em] uppercase text-white/35">
                      <Anchor className="w-3 h-3" /> /go/{f.slug}
                    </span>
                  </div>
                </div>
              </div>
            </a>
          );
        })}
      </div>

      {/* Lineage footer note */}
      <div
        className="mt-6 rounded-md border border-violet/15 bg-violet/[0.03] p-4 flex items-center gap-3"
        data-testid="lineage-law-footer"
      >
        <Sparkles className="w-4 h-4 text-violet shrink-0" />
        <p className="text-sm text-white/55 leading-relaxed">
          Every engagement routes through{" "}
          <span className="font-mono text-violet-soft">
            affiliate.eigooinc.net
          </span>{" "}
          — commissions cascade to Nessa under the{" "}
          <span className="text-gold">75/15/10 Lineage Law</span>: 75% to the
          originating node, 15% upstream, 10% to the Armada reserve.
        </p>
      </div>
    </section>
  );
}
